/**
 * AppLogsViewer Component
 *
 * CloudWatch logs viewer with filtering, search, and tabular display.
 * Features:
 * - Tabular format with columns: Timestamp, Level, Request ID, Message
 * - Grouped by requestId with visual separators
 * - Sorted DESC (most recent first)
 * - UTC-03:00 timestamp display with milliseconds
 */

import React, { useState, useMemo } from 'react';
import { useApplicationLogs } from '../hooks/useApplicationLogs';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import type { ApplicationLogsQuery, ApplicationLogEntry } from '../../../types/application.types';
import { formatLogTimestamp } from '../../../lib/utils/format';
import { getErrorMessage } from '../../../lib/utils/errors';

interface AppLogsViewerProps {
  tenantId: string;
  applicationId: string;
}

/**
 * Time range options for log filtering
 */
const TIME_RANGES = [
  { value: '1h', label: 'Last hour', minutes: 60 },
  { value: '6h', label: 'Last 6 hours', minutes: 360 },
  { value: '24h', label: 'Last 24 hours', minutes: 1440 },
  { value: '7d', label: 'Last 7 days', minutes: 10080 },
  { value: 'custom', label: 'Custom range', minutes: 0 },
];

/**
 * AppLogsViewer component
 */
/**
 * Get CSS class for log level badge
 */
const getLevelColor = (level?: string): string => {
  switch (level?.toLowerCase()) {
    case 'error':
      return 'text-red-400 bg-red-400/10';
    case 'warn':
      return 'text-yellow-400 bg-yellow-400/10';
    case 'info':
      return 'text-blue-400 bg-blue-400/10';
    case 'debug':
      return 'text-gray-400 bg-gray-400/10';
    default:
      return 'text-gray-400 bg-gray-400/10';
  }
};

export const AppLogsViewer: React.FC<AppLogsViewerProps> = ({
  tenantId,
  applicationId,
}) => {
  const [timeRange, setTimeRange] = useState<string>('1h');
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [customStartTime, setCustomStartTime] = useState<string>('');
  const [customEndTime, setCustomEndTime] = useState<string>('');
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Auto-refresh: increment counter every 30 seconds to recompute time window
  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCounter((c) => c + 1);
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Debounce search text
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Build query parameters
  const query = useMemo<ApplicationLogsQuery>(() => {
    const selectedRange = TIME_RANGES.find((r) => r.value === timeRange);
    const now = Date.now();

    let startTime: number | undefined;
    let endTime: number | undefined;

    if (timeRange === 'custom') {
      if (customStartTime) {
        startTime = new Date(customStartTime).getTime();
      }
      if (customEndTime) {
        endTime = new Date(customEndTime).getTime();
      }
    } else if (selectedRange && selectedRange.minutes > 0) {
      startTime = now - selectedRange.minutes * 60 * 1000;
      endTime = now;
    }

    return {
      startTime,
      endTime,
      limit: 1000,
      searchTerm: debouncedSearch || undefined,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, debouncedSearch, customStartTime, customEndTime, refreshCounter]);

  // Fetch logs
  const { data, isLoading, error, refetch } = useApplicationLogs(
    tenantId,
    applicationId,
    query
  );

  // Sort logs DESC (most recent first) and group by requestId
  const groupedLogs = useMemo(() => {
    if (!data?.logs) return new Map<string, ApplicationLogEntry[]>();

    // Sort DESC by timestamp
    const sorted = [...data.logs].sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    // Group by requestId while maintaining sort order
    const groups = new Map<string, ApplicationLogEntry[]>();
    sorted.forEach(log => {
      const key = log.requestId || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(log);
    });

    return groups;
  }, [data?.logs]);

  const handleRefresh = () => {
    setRefreshCounter((c) => c + 1);
  };

  const handleDownloadLogs = () => {
    if (!data?.logs || data.logs.length === 0) {
      return;
    }

    // Use grouped logs (already sorted DESC)
    const logsText = Array.from(groupedLogs.entries())
      .flatMap(([, logs]) => logs)
      .map((log) => `${formatLogTimestamp(log.timestamp)} | ${log.level?.toUpperCase() || '-'} | ${log.requestId || '-'} | ${log.message}`)
      .join('\n');

    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `application-${applicationId}-logs-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Time Range */}
          <div>
            <Select
              label="Time Range"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              options={TIME_RANGES.map((range) => ({
                value: range.value,
                label: range.label,
              }))}
            />
          </div>

          {/* Search */}
          <div>
            <Input
              label="Search Logs"
              placeholder="Filter by message content..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        {/* Custom Time Range */}
        {timeRange === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                type="datetime-local"
                label="Start Time"
                value={customStartTime}
                onChange={(e) => setCustomStartTime(e.target.value)}
              />
            </div>
            <div>
              <Input
                type="datetime-local"
                label="End Time"
                value={customEndTime}
                onChange={(e) => setCustomEndTime(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={handleRefresh}>
            Refresh
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleDownloadLogs}
            disabled={!data?.logs || data.logs.length === 0}
          >
            Download Logs
          </Button>
          {data?.logs && (
            <span className="text-sm text-gray-600 ml-auto">
              {data.logs.length} log entries
            </span>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Alert variant="error">
          {getErrorMessage(error)}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Logs Display */}
      {!isLoading && data && (
        <>
          {data.logs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No logs found for the selected time range.</p>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg max-h-[600px] overflow-auto">
              <table className="w-full font-mono text-sm">
                <thead className="sticky top-0 bg-gray-800">
                  <tr className="text-left text-gray-400 text-xs uppercase">
                    <th className="px-3 py-2 whitespace-nowrap">Timestamp (UTC-03:00)</th>
                    <th className="px-3 py-2 w-20">Level</th>
                    <th className="px-3 py-2 w-24">Request ID</th>
                    <th className="px-3 py-2">Message</th>
                  </tr>
                </thead>
                <tbody className="text-gray-100">
                  {Array.from(groupedLogs.entries()).map(([requestId, logs], groupIndex) => (
                    <React.Fragment key={requestId}>
                      {groupIndex > 0 && (
                        <tr>
                          <td colSpan={4} className="py-1">
                            <div className="border-t border-gray-700" />
                          </td>
                        </tr>
                      )}
                      {logs.map((log, index) => (
                        <tr key={`${requestId}-${index}`} className="hover:bg-gray-800 transition-colors">
                          <td className="px-3 py-1.5 text-gray-400 whitespace-nowrap">
                            {formatLogTimestamp(log.timestamp)}
                          </td>
                          <td className="px-3 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getLevelColor(log.level)}`}>
                              {log.level?.toUpperCase() || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-gray-500 font-mono text-xs">
                            {index === 0 ? (log.requestId?.slice(0, 8) || '-') : ''}
                          </td>
                          <td className="px-3 py-1.5 text-gray-100 break-words">
                            {log.message}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Info */}
          {data.nextToken && (
            <Alert variant="info">
              More logs available. Adjust filters or time range to see additional entries.
            </Alert>
          )}
        </>
      )}
    </div>
  );
};
